/**
 * SEO Scanner API Service
 * Frontend service for SEO analysis
 */

import apiClient from './api.service';

// Types
export type SEOSeverity = 'critical' | 'warning' | 'info' | 'pass';

export type SEOCategory =
  | 'meta_tags'
  | 'headings'
  | 'images'
  | 'social'
  | 'schema'
  | 'mobile'
  | 'performance'
  | 'links'
  | 'content'
  | 'technical'
  | 'accessibility';

export interface SEOIssue {
  id: string;
  severity: SEOSeverity;
  category: SEOCategory;
  title: string;
  description: string;
  element?: string;
  currentValue?: string;
  recommendedValue?: string;
  impact: string;
  howToFix: string;
}

export interface MetaTagsAnalysis {
  title: {
    value: string | null;
    length: number;
    issues: SEOIssue[];
  };
  description: {
    value: string | null;
    length: number;
    issues: SEOIssue[];
  };
  keywords: string | null;
  canonical: string | null;
  robots: string | null;
  viewport: string | null;
  charset: string | null;
  language: string | null;
}

export interface HeadingsAnalysis {
  h1: { count: number; values: string[] };
  h2: { count: number; values: string[] };
  h3: { count: number; values: string[] };
  h4: { count: number };
  h5: { count: number };
  h6: { count: number };
  hasProperHierarchy: boolean;
  issues: SEOIssue[];
  structure?: HeadingStructureItem[];
  issueDetails?: HeadingIssueDetail[];
}

export interface ImageAnalysis {
  total: number;
  withAlt: number;
  withoutAlt: number;
  withEmptyAlt: number;
  lazyLoaded: number;
  images: {
    src: string;
    alt: string | null;
    width?: number;
    height?: number;
    hasLazyLoad: boolean;
  }[];
  issues: SEOIssue[];
}

export interface SocialTagsAnalysis {
  openGraph: {
    title: string | null;
    description: string | null;
    image: string | null;
    url: string | null;
    type: string | null;
    siteName: string | null;
  };
  twitter: {
    card: string | null;
    title: string | null;
    description: string | null;
    image: string | null;
    site: string | null;
    creator: string | null;
  };
  issues: SEOIssue[];
}

export interface SchemaAnalysis {
  hasSchema: boolean;
  schemaTypes: string[];
  schemas: { type: string; data: any }[];
  issues: SEOIssue[];
}

export interface LinkAnalysis {
  internal: {
    count: number;
    links: { href: string; text: string; nofollow: boolean }[];
  };
  external: {
    count: number;
    links: { href: string; text: string; nofollow: boolean }[];
  };
  broken: string[];
  redirects: string[];
  issues: SEOIssue[];
}

export interface ContentAnalysis {
  wordCount: number;
  readingTime: number;
  textToHtmlRatio: number;
  paragraphs: number;
  sentences: number;
  averageSentenceLength: number;
  issues: SEOIssue[];
}

export interface TechnicalAnalysis {
  hasRobotsTxt: boolean;
  robotsTxtContent?: string;
  hasSitemapXml: boolean;
  sitemapUrl?: string;
  hasHttps: boolean;
  hasWwwRedirect: boolean;
  compression: string | null;
  cacheHeaders: string | null;
  serverResponseTime: number;
  issues: SEOIssue[];
}

export interface MobileAnalysis {
  hasViewportMeta: boolean;
  viewportContent: string | null;
  hasMobileOptimizedContent: boolean;
  touchIconUrl: string | null;
  issues: SEOIssue[];
}

export interface PerformanceHints {
  largeImages: string[];
  renderBlockingResources: string[];
  externalScripts: number;
  inlineStyles: number;
  totalCssSize: number;
  totalJsSize: number;
  issues: SEOIssue[];
}

export interface AccessibilityBasics {
  hasSkipLink: boolean;
  hasLandmarks: boolean;
  formsWithLabels: boolean;
  colorContrastIssues: number;
  issues: SEOIssue[];
}

// PageSpeed / Core Web Vitals Types
export interface CoreWebVitals {
  lcp: { value: number; rating: 'good' | 'needs-improvement' | 'poor'; element?: string };
  fid: { value: number; rating: 'good' | 'needs-improvement' | 'poor' };
  cls: { value: number; rating: 'good' | 'needs-improvement' | 'poor'; elements?: string[] };
  fcp: { value: number; rating: 'good' | 'needs-improvement' | 'poor' };
  ttfb: { value: number; rating: 'good' | 'needs-improvement' | 'poor' };
  inp: { value: number; rating: 'good' | 'needs-improvement' | 'poor' };
  tti: { value: number; rating: 'good' | 'needs-improvement' | 'poor' };
  tbt: { value: number; rating: 'good' | 'needs-improvement' | 'poor' };
  speedIndex: { value: number; rating: 'good' | 'needs-improvement' | 'poor' };
}

export interface PageSpeedOpportunity {
  id: string;
  title: string;
  description: string;
  savings: { time?: number; bytes?: number };
  priority: 'high' | 'medium' | 'low';
}

export interface PageSpeedDiagnostic {
  id: string;
  title: string;
  description: string;
  details?: any;
}

export interface PageSpeedScreenshot {
  data: string; // base64 encoded image
  width: number;
  height: number;
  mimeType: string;
}

export interface PageSpeedAnalysis {
  score: number;
  coreWebVitals: CoreWebVitals;
  opportunities: PageSpeedOpportunity[];
  diagnostics: PageSpeedDiagnostic[];
  resources: {
    totalSize: number;
    htmlSize: number;
    cssSize: number;
    jsSize: number;
    imageSize: number;
    fontSize: number;
    otherSize: number;
  };
  issues: SEOIssue[];
  // Screenshots captured during analysis
  screenshot?: PageSpeedScreenshot;
  fullPageScreenshot?: PageSpeedScreenshot;
  // Set to true when data comes from a fallback due to API error
  error?: boolean;
  errorMessage?: string;
}

export interface HeadingStructureItem {
  level: number;
  text: string;
  position: number;
  isProperOrder: boolean;
  issue?: string;
}

export interface HeadingIssueDetail {
  type: 'missing_h1' | 'multiple_h1' | 'skipped_level' | 'empty_heading' | 'too_long' | 'duplicate';
  level?: number;
  expectedLevel?: number;
  foundLevel?: number;
  text?: string;
  position?: number;
}

export interface SEOScore {
  overall: number;
  categories: {
    metaTags: number;
    headings: number;
    images: number;
    social: number;
    schema: number;
    links: number;
    content: number;
    technical: number;
    mobile: number;
    performance: number;
    accessibility: number;
  };
}

export interface SEOScanResult {
  url: string;
  scannedAt: string;
  duration: number;
  score: SEOScore;
  metaTags: MetaTagsAnalysis;
  headings: HeadingsAnalysis;
  images: ImageAnalysis;
  social: SocialTagsAnalysis;
  schema: SchemaAnalysis;
  links: LinkAnalysis;
  content: ContentAnalysis;
  technical: TechnicalAnalysis;
  mobile: MobileAnalysis;
  performance: PerformanceHints;
  accessibility: AccessibilityBasics;
  pageSpeed?: PageSpeedAnalysis; // Legacy - for backwards compatibility
  pageSpeedMobile?: PageSpeedAnalysis;
  pageSpeedDesktop?: PageSpeedAnalysis;
  allIssues: SEOIssue[];
  passedChecks: string[];
  recommendations: string[];
}

export interface SEOCategory {
  id: string;
  name: string;
  description: string;
  weight: number;
}

export interface SEOBestPractice {
  rule: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
}

// API Functions
export const seoScannerApi = {
  /**
   * Scan a URL for SEO issues
   */
  async scan(
    url: string,
    options?: {
      checkLinks?: boolean;
      checkRobots?: boolean;
      checkSitemap?: boolean;
      maxImages?: number;
      timeout?: number;
    }
  ): Promise<SEOScanResult> {
    try {
      // apiClient.post returns response.data directly (the response body)
      const response = await apiClient.post<{ success: boolean; data: SEOScanResult; error?: string }>(
        '/v1/seo-scanner/scan',
        { url, options }
      );

      console.log('[SEO Scanner] API Response:', response);

      if (!response) {
        throw new Error('EMPTY_RESPONSE');
      }

      if (!response.success) {
        throw new Error(response.error || 'SCAN_ERROR');
      }

      if (!response.data) {
        throw new Error('NO_DATA');
      }

      const result = response.data;

      // Validate the result has required fields
      if (!result.score) {
        console.error('[SEO Scanner] Invalid result - missing score:', result);
        throw new Error('INVALID_RESULT');
      }

      return result;
    } catch (error: any) {
      console.error('[SEO Scanner] Scan error:', error);

      // Re-throw with a standardized error code
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      if (error.message) {
        throw error;
      }
      throw new Error('SCAN_ERROR');
    }
  },

  /**
   * Get list of SEO check categories
   */
  async getCategories(): Promise<SEOCategory[]> {
    const response = await apiClient.get<{ success: boolean; data: { categories: SEOCategory[] } }>(
      '/v1/seo-scanner/categories'
    );
    return response.data.categories;
  },

  /**
   * Get SEO best practices guide
   */
  async getBestPractices(): Promise<{ category: string; items: SEOBestPractice[] }[]> {
    const response = await apiClient.get<{ success: boolean; data: { practices: { category: string; items: SEOBestPractice[] }[] } }>(
      '/v1/seo-scanner/best-practices'
    );
    return response.data.practices;
  },

  /**
   * Get scan history
   */
  async getScans(params?: { limit?: number; offset?: number }): Promise<{
    scans: Array<{
      id: string;
      url: string;
      status: string;
      overall_score: number;
      critical_issues: number;
      warning_issues: number;
      info_issues: number;
      passed_checks: number;
      duration_ms: number;
      created_at: string;
      completed_at: string;
    }>;
    total: number;
    limit: number;
    offset: number;
  }> {
    const response = await apiClient.get<{ success: boolean; data: any }>('/v1/seo-scanner/scans', { params });
    return response.data;
  },

  /**
   * Get a specific scan by ID
   */
  async getScan(id: string): Promise<SEOScanResult> {
    const response = await apiClient.get<{ success: boolean; data: SEOScanResult }>(
      `/v1/seo-scanner/scans/${id}`
    );
    return response.data;
  },

  /**
   * Delete a scan
   */
  async deleteScan(id: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete<{ success: boolean; message: string }>(
      `/v1/seo-scanner/scans/${id}`
    );
    return response;
  },

  /**
   * Export scan as PDF or Excel (file download)
   */
  async exportScan(id: string, format: 'pdf' | 'excel'): Promise<void> {
    const response = await apiClient.get(
      `/v1/seo-scanner/scans/${id}/export/${format}`,
      { responseType: 'blob' }
    );

    // Create download link
    const blob = new Blob([response], {
      type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `seo-report.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Export current scan result directly (without saving)
   */
  async exportScanDirect(scanResult: SEOScanResult, format: 'pdf' | 'excel'): Promise<void> {
    const response = await apiClient.post(
      '/v1/seo-scanner/export',
      { scanResult, format },
      { responseType: 'blob' }
    );

    // Create download link
    const blob = new Blob([response], {
      type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Generate filename from URL
    let urlHost = 'scan';
    try {
      const parsedUrl = new URL(scanResult.url);
      urlHost = parsedUrl.hostname.replace(/\./g, '_');
    } catch (e) {
      // Use default
    }
    const timestamp = new Date().toISOString().split('T')[0];
    link.download = `seo-report-${urlHost}-${timestamp}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },

  /**
   * Get SEO dashboard summary
   */
  async getDashboard(): Promise<{
    totalScans: number;
    averageScore: number;
    totalIssues: {
      critical: number;
      warnings: number;
      info: number;
    };
    recentScans: Array<{
      id: string;
      url: string;
      overall_score: number;
      created_at: string;
    }>;
  }> {
    const response = await apiClient.get<{ success: boolean; data: any }>('/v1/seo-scanner/dashboard');
    return response.data;
  },
};

export default seoScannerApi;
